
import { useState } from 'react';
import type { Node, Edge } from '@xyflow/react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { LogicFlow } from '@/components/logic-engine/types';
import { useDialogStore } from '@/store/dialogStore';

type SaveFlowParams = {
  name: string;
  manufacturer?: string;
  vehicle_type?: string;
  nodes: Node[];
  edges: Edge[];
};

type LoadFlowResult = {
  id: string;
  name: string;
  manufacturer?: string;
  vehicle_type?: string;
  nodes: Node[];
  edges: Edge[];
  version?: string;
};

export function useLogicFlows() {
  const { toast } = useToast();
  const [flows, setFlows] = useState<LogicFlow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { open: openDialog } = useDialogStore();

  const saveFlow = async ({ name, manufacturer, vehicle_type, nodes, edges }: SaveFlowParams) => {
    try {
      setIsLoading(true);
      
      // Convert Node[] and Edge[] to JSON-compatible format
      const flowData = {
        name,
        manufacturer,
        vehicle_type,
        // Ensure nodes and edges are JSON serializable
        nodes: JSON.parse(JSON.stringify(nodes)),
        edges: JSON.parse(JSON.stringify(edges)),
        version: '1.0'
      };
      
      const { data, error } = await supabase
        .from('logic_flows')
        .insert(flowData)
        .select()
        .single();
      
      if (error) throw error;
      
      toast({
        title: 'Flow saved',
        description: `"${name}" has been saved successfully`
      });
      
      return data;
    } catch (error) {
      console.error('Error saving flow:', error);
      toast({
        title: 'Error',
        description: 'Failed to save flow. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadFlows = async () => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('logic_flows')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Convert the data to the expected type
      const typedFlows = data.map(flow => ({
        ...flow,
        nodes: flow.nodes as unknown as Node[],
        edges: flow.edges as unknown as Edge[]
      })) as LogicFlow[];
      
      setFlows(typedFlows);

      // Display the flow selection dialog
      if (typedFlows.length > 0) {
        openDialog({
          title: 'Select a Flow',
          content: 'flows',
          data: { flows: typedFlows }
        });
      } else {
        toast({
          title: 'No flows found',
          description: 'You have not saved any flows yet.'
        });
      }
      
      return typedFlows;
    } catch (error) {
      console.error('Error loading flows:', error);
      toast({
        title: 'Error',
        description: 'Failed to load flows. Please try again.',
        variant: 'destructive'
      });
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const loadFlowById = async (id: string): Promise<LoadFlowResult | null> => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('logic_flows')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      
      // Convert the data to the expected type
      const flowData = {
        ...data,
        nodes: data.nodes as unknown as Node[],
        edges: data.edges as unknown as Edge[]
      } as LoadFlowResult;
      
      return flowData;
    } catch (error) {
      console.error('Error loading flow:', error);
      toast({
        title: 'Error',
        description: 'Failed to load flow. Please try again.',
        variant: 'destructive'
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const clearFlow = () => {
    // This function will be used by consumers like LogicFlowEditor
    toast({
      title: 'Flow cleared',
      description: 'All nodes and connections have been removed'
    });
  };

  return {
    flows,
    isLoading,
    saveFlow,
    loadFlows,
    loadFlowById,
    clearFlow
  };
}
